// Lib
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Utils
import platformSingleton from '../platform/platformSingleton';
import { isPwa } from '../platform/utils/platformAppTypeUtils';

// Constants
import { PWA_APP_INSTALLED } from './store/pwaAppConstants';

const PWA_DISPLAY_MODE_QUERIES = ['(display-mode: standalone)', '(display-mode: window-controls-overlay)'];

const mapDispatchToProps = (dispatch) => ({
    dispatchPwaAppInstall: (platform) => dispatch({ type: PWA_APP_INSTALLED, platform }),
});

/**
 * Tells the app it's now running as a PWA when that changes without a page reload.
 * E.g. straight after install, or when "Open in app" moves a browser tab into the PWA window.
 */
const PwaDetection = (props) => {
    const { dispatchPwaAppInstall } = props;

    const detectPwa = React.useCallback(() => {
        if (!isPwa()) return;

        dispatchPwaAppInstall(platformSingleton.type);
    }, [dispatchPwaAppInstall]);

    // Detects the PWA once it has been installed
    React.useEffect(() => {
        const controller = new AbortController();

        const onAppInstalled = () => {
            // Give the app a chance to complete installation
            setTimeout(detectPwa, 2000);
        };

        window.addEventListener('appinstalled', onAppInstalled, { signal: controller.signal });

        return () => controller.abort();
    }, [detectPwa]);

    // Detects the PWA when "Open in app" moves the browser tab into the PWA window
    React.useEffect(() => {
        if (!window.matchMedia) return;

        const controller = new AbortController();

        PWA_DISPLAY_MODE_QUERIES.forEach((query) => {
            const mediaQueryList = window.matchMedia(query);

            // Safari before 14 doesn't support this, but it can't move a tab into a PWA either
            if (!mediaQueryList.addEventListener) return;

            mediaQueryList.addEventListener('change', detectPwa, { signal: controller.signal });
        });

        return () => controller.abort();
    }, [detectPwa]);

    return null;
};

PwaDetection.propTypes = {
    dispatchPwaAppInstall: PropTypes.func,
};

export default connect(null, mapDispatchToProps)(PwaDetection);
